import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

// ── STYLES ────────────────────────────────────────────────────────────
const S = {
  primaryBtn: { background:"#8B6A50", color:"white", border:"none", borderRadius:8, padding:"10px 20px", fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit" },
  ghostBtn:   { background:"white", border:"0.5px solid #D4C4B0", borderRadius:8, padding:"8px 14px", fontSize:12, color:"#6E5C4A", cursor:"pointer", fontFamily:"inherit" },
  input:      { background:"#F5EFE8", border:"0.5px solid #D8C8B0", borderRadius:9, padding:"10px 13px", fontSize:13, color:"#3A2E26", width:"100%", fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  card:       { background:"white", borderRadius:14, border:"0.5px solid #E4D8C8", padding:16, marginBottom:14 },
  sectionLbl: { fontSize:10, textTransform:"uppercase", letterSpacing:"0.1em", color:"#9A8070", fontWeight:500, marginBottom:8, display:"block" },
};

const TYPE_COLORS = {
  moment:    { bg:"#EEF5EE", color:"#4A7060", label:"Moment" },
  photo:     { bg:"#EDF2F8", color:"#4A6080", label:"Photo" },
  milestone: { bg:"#FBF3E8", color:"#8B6A30", label:"Milestone" },
  recipe:    { bg:"#F8EDEE", color:"#8B4A5E", label:"Recipe" },
  video:     { bg:"#EEF0FB", color:"#504A8B", label:"Video" },
};

const NUDGE_REASONS = [
  "📞 Call overdue — it's been a while!",
  "🎂 Birthday coming up — call them first!",
  "💙 Just a warm check-in nudge",
  "🎉 Big family news to celebrate!",
  "💌 Christmas card reminder!",
  "🙏 Thank you card — send some love!",
];

// ── HELPERS ───────────────────────────────────────────────────────────
function Avatar({ member, size=32 }) {
  if (!member) return <div style={{ width:size, height:size, borderRadius:"50%", background:"#E0D0C0" }} />;
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:member.bg||"#E0D0C0", color:member.color||"#8B6A50", display:"flex", alignItems:"center", justifyContent:"center", fontSize:Math.floor(size*0.34), fontWeight:500, flexShrink:0 }}>
      {member.initials||"?"}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

// ── SIGN UP SCREEN ────────────────────────────────────────────────────
function SignUpScreen({ onBack, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = pick name, 2 = fill details
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [form, setForm] = useState({
    password: '', confirmPassword: '',
    birthday: '', email: '',
    phone: '', address: '', city: '', state: '', zip: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    // Load family members who don't have an auth account yet
    supabase.from('family_members')
      .select('*')
      .is('email', null)   // not yet registered
      .order('name')
      .then(({ data }) => {
        setFamilyMembers(data || []);
        setLoadingMembers(false);
      });
  }, []);

  const filtered = familyMembers.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase())
  );

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleRegister = async () => {
    setError('');
    const { password, confirmPassword, birthday, email, phone, address, city, state, zip } = form;

    if (!password || password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords don\'t match.'); return; }
    if (!birthday) { setError('Please enter your birthday.'); return; }
    if (!email || !email.includes('@')) { setError('Please enter a valid email address.'); return; }
    if (!address.trim() || !city.trim() || !state.trim() || !zip.trim()) { setError('Please enter your full address (needed for sending cards!).'); return; }

    setLoading(true);

    // Create Supabase auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password: password.trim(),
    });

    if (authError) {
      setError(authError.message || 'Could not create account. Try a different email.');
      setLoading(false);
      return;
    }

    const userId = authData.user?.id;
    if (!userId) { setError('Something went wrong. Please try again.'); setLoading(false); return; }

    // Update the family_members row with all profile info
    const fullAddress = `${address.trim()}, ${city.trim()}, ${state.trim()} ${zip.trim()}`;
    const { error: profileError } = await supabase.from('family_members').update({
      id: userId,          // link to auth user
      email: email.trim().toLowerCase(),
      phone: phone.trim() || null,
      address: fullAddress,
      birthday: birthday,
    }).eq('id', selectedMember.id);

    if (profileError) {
      // If the member row uses a different id field, try inserting
      await supabase.from('family_members').upsert({
        id: userId,
        name: selectedMember.name,
        initials: selectedMember.initials,
        color: selectedMember.color,
        bg: selectedMember.bg,
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        address: fullAddress,
        birthday: birthday,
      });
    }

    setLoading(false);
    onSuccess && onSuccess();
  };

  const FONT = `@import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=DM+Sans:wght@300;400;500&display=swap');`;

  const sharedWrap = {
    minHeight:"100vh", background:"#F5EFE8", display:"flex",
    alignItems:"flex-start", justifyContent:"center", padding:"32px 20px",
    fontFamily:"'DM Sans',sans-serif",
  };

  const card = {
    background:"white", borderRadius:20, border:"0.5px solid #E4D8C8",
    padding:"32px 28px", width:"100%", maxWidth:440,
  };

  // ── STEP 1: Pick your name ──
  if (step === 1) return (
    <div style={sharedWrap}>
      <style>{FONT}</style>
      <div style={card}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"#9A8070", fontSize:12, cursor:"pointer", marginBottom:20, padding:0, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
          ← Back to sign in
        </button>
        <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:28, color:"#3A2E26", marginBottom:4 }}>Create your login</div>
        <div style={{ fontSize:13, color:"#9A8070", marginBottom:24, lineHeight:1.6 }}>
          First, find your name in the family list below.
        </div>

        <input
          style={{ ...S.input, marginBottom:14 }}
          placeholder="Search your name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {loadingMembers ? (
          <div style={{ textAlign:"center", padding:24, color:"#9A8070", fontSize:13 }}>Loading family…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:24, color:"#9A8070", fontSize:13, lineHeight:1.6 }}>
            {search ? `No match for "${search}"` : 'Everyone has already registered!'}<br/>
            <span style={{ fontSize:12 }}>Ask Anika if you think there's a mistake.</span>
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, maxHeight:320, overflowY:"auto" }}>
            {filtered.map(m => (
              <button key={m.id} onClick={() => { setSelectedMember(m); setStep(2); }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background:"#FDFAF7", border:"0.5px solid #E4D8C8", borderRadius:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s", textAlign:"left" }}
                onMouseOver={e => { e.currentTarget.style.background='#F5EFE8'; e.currentTarget.style.borderColor='#C4A880'; }}
                onMouseOut={e => { e.currentTarget.style.background='#FDFAF7'; e.currentTarget.style.borderColor='#E4D8C8'; }}>
                <Avatar member={m} size={32} />
                <span style={{ fontSize:13, color:"#3A2E26", fontWeight:500 }}>{m.name}</span>
              </button>
            ))}
          </div>
        )}

        <div style={{ marginTop:20, paddingTop:16, borderTop:"0.5px solid #F0E8DC", fontSize:11, color:"#B0A090", textAlign:"center" }}>
          kulandercollins.com · Private family space 💛
        </div>
      </div>
    </div>
  );

  // ── STEP 2: Fill in details ──
  return (
    <div style={sharedWrap}>
      <style>{FONT}</style>
      <div style={{ ...card, maxWidth:480 }}>
        <button onClick={() => setStep(1)} style={{ background:"none", border:"none", color:"#9A8070", fontSize:12, cursor:"pointer", marginBottom:20, padding:0, fontFamily:"inherit", display:"flex", alignItems:"center", gap:4 }}>
          ← Choose a different name
        </button>

        {/* Selected member banner */}
        <div style={{ display:"flex", alignItems:"center", gap:12, background:"#F5EFE8", borderRadius:12, padding:"12px 14px", marginBottom:24, border:"0.5px solid #E0D0C0" }}>
          <Avatar member={selectedMember} size={40} />
          <div>
            <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:22, color:"#3A2E26" }}>Hi, {selectedMember.name.split(' ')[0]}! 👋</div>
            <div style={{ fontSize:12, color:"#9A8070", marginTop:1 }}>Set up your family portal login</div>
          </div>
        </div>

        {/* PASSWORD */}
        <div style={{ marginBottom:16 }}>
          <label style={S.sectionLbl}>🔒 Create a Password</label>
          <input style={{ ...S.input, marginBottom:8 }} type="password" placeholder="At least 6 characters"
            value={form.password} onChange={e => setField('password', e.target.value)} />
          <input style={S.input} type="password" placeholder="Confirm password"
            value={form.confirmPassword} onChange={e => setField('confirmPassword', e.target.value)} />
        </div>

        {/* BIRTHDAY */}
        <div style={{ marginBottom:16 }}>
          <label style={S.sectionLbl}>🎂 Your Birthday <span style={{ color:"#C46A30" }}>*</span></label>
          <div style={{ fontSize:11, color:"#9A8070", marginBottom:6 }}>This shows up on the family calendar so everyone can celebrate you!</div>
          <input style={S.input} type="date"
            value={form.birthday} onChange={e => setField('birthday', e.target.value)} />
        </div>

        {/* EMAIL */}
        <div style={{ marginBottom:16 }}>
          <label style={S.sectionLbl}>📧 Email Address <span style={{ color:"#C46A30" }}>*</span></label>
          <input style={S.input} type="email" placeholder="your@email.com"
            value={form.email} onChange={e => setField('email', e.target.value)} autoCapitalize="none" />
        </div>

        {/* PHONE */}
        <div style={{ marginBottom:16 }}>
          <label style={S.sectionLbl}>📞 Phone Number <span style={{ color:"#9A8070", fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
          <input style={S.input} type="tel" placeholder="(555) 555-5555"
            value={form.phone} onChange={e => setField('phone', e.target.value)} />
        </div>

        {/* ADDRESS */}
        <div style={{ marginBottom:20 }}>
          <label style={S.sectionLbl}>🏠 Mailing Address <span style={{ color:"#C46A30" }}>*</span></label>
          <div style={{ fontSize:11, color:"#9A8070", marginBottom:6 }}>So the family can send birthday cards, holiday cards, and more 💌</div>
          <input style={{ ...S.input, marginBottom:8 }} placeholder="Street address"
            value={form.address} onChange={e => setField('address', e.target.value)} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 90px", gap:8 }}>
            <input style={S.input} placeholder="City"
              value={form.city} onChange={e => setField('city', e.target.value)} />
            <input style={S.input} placeholder="State"
              value={form.state} onChange={e => setField('state', e.target.value)} />
            <input style={S.input} placeholder="ZIP"
              value={form.zip} onChange={e => setField('zip', e.target.value)} />
          </div>
        </div>

        {error && (
          <div style={{ fontSize:12, color:"#C46A30", background:"#FBF3E8", border:"0.5px solid #E8D8B8", borderRadius:8, padding:"8px 12px", marginBottom:14 }}>
            {error}
          </div>
        )}

        <button
          style={{ ...S.primaryBtn, width:"100%", padding:"13px", fontSize:15, opacity: loading ? 0.7 : 1 }}
          onClick={handleRegister}
          disabled={loading}>
          {loading ? 'Creating your login…' : 'Join the Family Portal 💛'}
        </button>

        <div style={{ marginTop:14, fontSize:11, color:"#B0A090", textAlign:"center", lineHeight:1.6 }}>
          Your info is only visible to family members.<br/>
          kulandercollins.com · Private family space
        </div>
      </div>
    </div>
  );
}

// ── LOGIN SCREEN ──────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpDone, setSignUpDone] = useState(false);

  if (showSignUp) {
    return (
      <SignUpScreen
        onBack={() => setShowSignUp(false)}
        onSuccess={() => { setShowSignUp(false); setSignUpDone(true); }}
      />
    );
  }

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError('Please enter your username and password.'); return; }
    setLoading(true); setError('');

    const loginEmail = email.includes('@') ? email.trim() : `${email.trim().toLowerCase()}@kulandercollins.com`;

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: password.trim(),
    });

    if (authError) {
      setError('Username or password not recognised. Try again or ask Anika for help.');
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:"#F5EFE8", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=DM+Sans:wght@300;400;500&display=swap');`}</style>
      <div style={{ background:"white", borderRadius:20, border:"0.5px solid #E4D8C8", padding:"36px 32px", width:"100%", maxWidth:400, textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🏡</div>
        <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:32, color:"#3A2E26", lineHeight:1, marginBottom:6 }}>Family Portal</div>
        <div style={{ fontSize:11, color:"#9A8070", letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:32 }}>Kulander Collins</div>

        {signUpDone && (
          <div style={{ fontSize:12, color:"#4A7060", background:"#EEF5EE", border:"0.5px solid #B8D8C8", borderRadius:8, padding:"8px 12px", marginBottom:16, textAlign:"left" }}>
            ✅ Account created! You can now sign in.
          </div>
        )}

        <div style={{ textAlign:"left", marginBottom:14 }}>
          <label style={{ ...S.sectionLbl }}>Username or Email</label>
          <input style={{ ...S.input }} placeholder="e.g. anika" value={email}
            onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} autoCapitalize="none" />
        </div>
        <div style={{ textAlign:"left", marginBottom:20 }}>
          <label style={{ ...S.sectionLbl }}>Password</label>
          <input style={{ ...S.input }} type="password" placeholder="Your password" value={password}
            onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        </div>

        {error && <div style={{ fontSize:12, color:"#C46A30", background:"#FBF3E8", border:"0.5px solid #E8D8B8", borderRadius:8, padding:"8px 12px", marginBottom:14, textAlign:"left" }}>{error}</div>}

        <button style={{ ...S.primaryBtn, width:"100%", padding:"13px", fontSize:15, opacity: loading ? 0.7 : 1 }}
          onClick={handleLogin} disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in to the Family Portal'}
        </button>

        {/* CREATE LOGIN LINK */}
        <div style={{ marginTop:20, paddingTop:18, borderTop:"0.5px solid #F0E8DC" }}>
          <div style={{ fontSize:13, color:"#6E5C4A", marginBottom:10 }}>New to the portal?</div>
          <button
            onClick={() => setShowSignUp(true)}
            style={{ ...S.ghostBtn, width:"100%", padding:"11px", fontSize:13, borderColor:"#C4A880", color:"#8B6A50" }}>
            ✨ Create my login
          </button>
        </div>

        <div style={{ marginTop:20, fontSize:12, color:"#9A8070", lineHeight:1.65 }}>
          Your username is your first name.<br/>
          Ask Anika if you need help.
        </div>
        <div style={{ marginTop:24, paddingTop:20, borderTop:"0.5px solid #F0E8DC", fontSize:11, color:"#B0A090" }}>
          kulandercollins.com · Private family space 💛
        </div>
      </div>
    </div>
  );
}

// ── MAIN PORTAL ───────────────────────────────────────────────────────
function Portal({ user, profile, members, onLogout }) {
  const [activeTab, setActiveTab] = useState('home');
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [composing, setComposing] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [composeType, setComposeType] = useState('moment');
  const [expandedPost, setExpandedPost] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [nudgeTarget, setNudgeTarget] = useState(null);
  const [nudgeSent, setNudgeSent] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const getMember = useCallback((id) => members.find(m => m.id === id) || { name:"Unknown", initials:"??", color:"#999", bg:"#eee" }, [members]);

  // ── LOAD POSTS ──
  const loadPosts = useCallback(async () => {
    const { data: postsData } = await supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (!postsData) { setLoadingPosts(false); return; }

    const postIds = postsData.map(p => p.id);

    const { data: commentsData } = await supabase
      .from('comments')
      .select('*')
      .in('post_id', postIds.length ? postIds : [-1])
      .order('created_at', { ascending: true });

    const { data: reactionsData } = await supabase
      .from('reactions')
      .select('*')
      .in('post_id', postIds.length ? postIds : [-1]);

    const enriched = postsData.map(post => {
      const postComments = (commentsData || []).filter(c => c.post_id === post.id);
      const postReactions = (reactionsData || []).filter(r => r.post_id === post.id);
      const reactionCounts = {};
      const myReactions = [];
      postReactions.forEach(r => {
        reactionCounts[r.emoji] = (reactionCounts[r.emoji] || 0) + 1;
        if (r.member_id === user.id) myReactions.push(r.emoji);
      });
      return { ...post, comments: postComments, reactions: reactionCounts, myReactions };
    });

    setPosts(enriched);
    setLoadingPosts(false);
  }, [user.id]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    const channel = supabase.channel('family-feed')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, () => loadPosts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comments' }, () => loadPosts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reactions' }, () => loadPosts())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadPosts]);

  const handlePost = async () => {
    if (!composeText.trim()) return;
    const { error } = await supabase.from('posts').insert({
      member_id: user.id,
      type: composeType,
      text: composeText.trim(),
    });
    if (!error) { setComposeText(''); setComposing(false); loadPosts(); }
  };

  const handleReact = async (postId, emoji) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;
    const already = post.myReactions.includes(emoji);
    if (already) {
      await supabase.from('reactions').delete().eq('post_id', postId).eq('member_id', user.id).eq('emoji', emoji);
    } else {
      await supabase.from('reactions').insert({ post_id: postId, member_id: user.id, emoji });
    }
    loadPosts();
  };

  const addComment = async (postId) => {
    const text = commentInputs[postId];
    if (!text?.trim()) return;
    await supabase.from('comments').insert({ post_id: postId, member_id: user.id, text: text.trim() });
    setCommentInputs(prev => ({ ...prev, [postId]: '' }));
    loadPosts();
  };

  const sendNudge = async (reason) => {
    await supabase.from('nudges').insert({
      from_member_id: user.id,
      to_member_id: nudgeTarget.id,
      reason,
    });
    setNudgeSent(nudgeTarget.name.split(' ')[0]);
    setNudgeTarget(null);
    setTimeout(() => setNudgeSent(null), 4000);
  };

  // ── BIRTHDAY CALC — shows anyone with a birthday within the next 14 days
  // AND anyone whose birthday falls in the current calendar month
  const getBirthdays = () => {
    const now = new Date();
    const year = now.getFullYear();
    const currentMonth = now.getMonth();

    return members
      .filter(m => m.birthday)
      .map(m => {
        // Support both "Jan 3" style strings and ISO "YYYY-MM-DD" dates
        let monthIdx, day;
        if (m.birthday.includes('-')) {
          const d = new Date(m.birthday + 'T00:00:00');
          monthIdx = d.getMonth();
          day = d.getDate();
        } else {
          const parts = m.birthday.split(' ');
          const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          monthIdx = monthNames.findIndex(mn => parts[0].startsWith(mn));
          day = parseInt(parts[1]);
        }
        if (monthIdx === -1 || isNaN(day)) return null;

        let bday = new Date(year, monthIdx, day);
        if (bday < now) bday = new Date(year + 1, monthIdx, day);
        const diff = Math.ceil((bday - now) / (1000 * 60 * 60 * 24));

        // Show if within 14 days OR birthday is in the current month
        const inCurrentMonth = monthIdx === currentMonth;
        const comingUp = diff <= 14;

        if (!inCurrentMonth && !comingUp) return null;

        const displayDate = m.birthday.includes('-')
          ? new Date(m.birthday + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })
          : m.birthday;

        return { member: m, date: displayDate, days: diff, inCurrentMonth, comingUp };
      })
      .filter(Boolean)
      .sort((a, b) => a.days - b.days)
      .slice(0, 6);
  };

  const birthdays = getBirthdays();

  const TABS = [
    { id:"home", icon:"🏠", label:"Home" },
    { id:"moments", icon:"✨", label:"Moments" },
    { id:"photos", icon:"📸", label:"Photos" },
    { id:"recipes", icon:"🍴", label:"Recipes" },
    { id:"wisdom", icon:"🌿", label:"Wisdom" },
    { id:"calendar", icon:"📅", label:"Calendar" },
    { id:"chat", icon:"💬", label:"Chat" },
    { id:"tools", icon:"⚙️", label:"Tools" },
  ];

  const EMOJIS = ["❤️", "🎉", "🔥", "😭", "🥰", "🌟", "👏", "🙌", "🍋", "🌅"];

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", background:"#F5EFE8", minHeight:"100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=DM+Sans:wght@300;400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .fade-in { animation: fadeIn 0.3s ease forwards; }
        button { cursor:pointer; }
      `}</style>

      {/* HEADER */}
      <div style={{ background:"white", borderBottom:"0.5px solid #E4D8C8", padding:"13px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:50 }}>
        <div>
          <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:26, color:"#3A2E26", lineHeight:1 }}>Family Portal</div>
          <div style={{ fontSize:10, color:"#9A8070", letterSpacing:"0.14em", textTransform:"uppercase", marginTop:3 }}>Kulander Collins · kulandercollins.com</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {birthdays[0] && birthdays[0].days <= 14 && (
            <div style={{ fontSize:11, background:"#FBF3E8", color:"#8B6A30", padding:"4px 10px", borderRadius:20, fontWeight:500, border:"0.5px solid #E8D8B8" }}>
              🎂 {birthdays[0].member.name.split(' ')[0]} · {birthdays[0].days === 0 ? 'Today! 🎉' : `${birthdays[0].days}d`}
            </div>
          )}
          <div style={{ position:"relative" }}>
            <div onClick={() => setShowUserMenu(v => !v)} style={{ cursor:"pointer" }}>
              <Avatar member={profile} size={34} />
            </div>
            {showUserMenu && (
              <div style={{ position:"absolute", right:0, top:42, background:"white", border:"0.5px solid #E4D8C8", borderRadius:12, padding:"8px 0", minWidth:160, zIndex:100, boxShadow:"0 4px 20px rgba(0,0,0,0.1)" }}>
                <div style={{ padding:"8px 16px", fontSize:13, color:"#3A2E26", fontWeight:500, borderBottom:"0.5px solid #F0E8DC" }}>Hi, {profile?.name?.split(' ')[0]} 👋</div>
                <div style={{ padding:"8px 16px", fontSize:12, color:"#C46A30", cursor:"pointer", borderTop:"0.5px solid #F0E8DC" }} onClick={onLogout}>Sign out</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NAV */}
      <div style={{ background:"white", borderBottom:"0.5px solid #E4D8C8", display:"flex", padding:"3px 0" }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, padding:"7px 8px", borderRadius:10, border:"none", background: activeTab===tab.id ? "rgba(139,106,80,0.13)" : "none", flex:1, fontFamily:"inherit", transition:"background 0.15s" }}>
            <span style={{ fontSize:17 }}>{tab.icon}</span>
            <span style={{ fontSize:9, color: activeTab===tab.id ? "#8B6A50" : "#9A8070", fontWeight: activeTab===tab.id ? 500 : 400 }}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div style={{ maxWidth:680, margin:"0 auto", padding:"16px 14px 80px" }}>

        {nudgeSent && (
          <div className="fade-in" style={{ background:"#4A7060", color:"white", borderRadius:12, padding:"12px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:10, fontSize:13 }}>
            <span style={{ fontSize:20 }}>📳</span>
            <div><strong>{nudgeSent}</strong> was nudged! 💛</div>
          </div>
        )}

        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div>
            {/* NUDGE ROW */}
            <div style={{ ...S.card }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <span style={{ ...S.sectionLbl, marginBottom:0 }}>📳 Send a Nudge</span>
                <span style={{ fontSize:11, color:"#9A8070" }}>Remind someone to call</span>
              </div>
              <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4 }}>
                {members.filter(m => m.id !== user.id).map(m => (
                  <button key={m.id} onClick={() => setNudgeTarget(m)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, padding:"8px 10px", background:"none", border:"0.5px solid #E0D0C0", borderRadius:12, flexShrink:0, fontFamily:"inherit", transition:"background 0.15s" }}
                    onMouseOver={e => e.currentTarget.style.background='#F5EFE8'} onMouseOut={e => e.currentTarget.style.background='none'}>
                    <Avatar member={m} size={32} />
                    <span style={{ fontSize:9, color:"#6E5C4A", fontWeight:500, whiteSpace:"nowrap" }}>{m.name.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* BIRTHDAYS */}
            {birthdays.length > 0 && (
              <div style={{ ...S.card }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <span style={{ ...S.sectionLbl, marginBottom:0 }}>🎂 Birthdays This Month & Coming Up</span>
                  <span style={{ fontSize:10, color:"#9A8070" }}>Within 14 days</span>
                </div>
                <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:4 }}>
                  {birthdays.map(b => (
                    <div key={b.member.id} style={{ flexShrink:0, textAlign:"center", background:b.member.bg || "#F5EFE8", borderRadius:12, padding:"10px 12px", minWidth:80, border: b.days<=7 ? `1.5px solid ${b.member.color || '#8B6A50'}80` : "0.5px solid #E4D8C8" }}>
                      <Avatar member={b.member} size={28} />
                      <div style={{ fontSize:11, fontWeight:500, color:"#3A2E26", marginTop:5 }}>{b.member.name.split(' ')[0]}</div>
                      <div style={{ fontSize:10, color:"#9A8070", marginTop:1 }}>{b.date}</div>
                      {b.days === 0
                        ? <div style={{ fontSize:9, background:"#4A7060", color:"white", borderRadius:8, padding:"2px 6px", marginTop:3, fontWeight:500 }}>Today! 🎉</div>
                        : b.days <= 7
                          ? <div style={{ fontSize:9, background:b.member.color || "#8B6A50", color:"white", borderRadius:8, padding:"2px 6px", marginTop:3, fontWeight:500 }}>{b.days}d</div>
                          : b.inCurrentMonth
                            ? <div style={{ fontSize:9, color:"#9A8070", marginTop:3 }}>this month</div>
                            : null
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* COMPOSE */}
            {!composing ? (
              <div onClick={() => setComposing(true)} style={{ ...S.card, display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                <Avatar member={profile} size={32} />
                <span style={{ color:"#9A8070", fontSize:14 }}>Share something with the family first…</span>
              </div>
            ) : (
              <div style={{ ...S.card, border:"0.5px solid #C4A880" }}>
                <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
                  {Object.entries(TYPE_COLORS).map(([type, tc]) => (
                    <button key={type} onClick={() => setComposeType(type)} style={{ padding:"4px 12px", borderRadius:20, border:`1px solid ${composeType===type ? tc.color : "#D4C4B0"}`, background: composeType===type ? tc.bg : "transparent", color: composeType===type ? tc.color : "#9A8070", fontSize:12, fontWeight: composeType===type ? 500 : 400, fontFamily:"inherit" }}>
                      {tc.label}
                    </button>
                  ))}
                </div>
                <textarea value={composeText} onChange={e => setComposeText(e.target.value)} placeholder={`Share a ${composeType} with the family…`}
                  style={{ width:"100%", border:"none", fontSize:14, color:"#3A2E26", resize:"none", minHeight:80, lineHeight:1.65, background:"transparent", fontFamily:"inherit", outline:"none" }} />
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, borderTop:"0.5px solid #E4D8C8", marginTop:6 }}>
                  <button style={{ ...S.ghostBtn, fontSize:12 }}>📎 Photo / Video</button>
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ ...S.ghostBtn }} onClick={() => setComposing(false)}>Cancel</button>
                    <button style={{ ...S.primaryBtn }} onClick={handlePost}>Share with Family</button>
                  </div>
                </div>
              </div>
            )}

            {loadingPosts && (
              <div style={{ textAlign:"center", padding:32, color:"#9A8070", fontSize:13 }}>Loading family feed…</div>
            )}

            {posts.map(post => {
              const m = getMember(post.member_id);
              const tc = TYPE_COLORS[post.type] || TYPE_COLORS.moment;
              const isExp = expandedPost === post.id;
              return (
                <div key={post.id} className="fade-in" style={{ ...S.card }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10 }}>
                    <Avatar member={m} size={38} />
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ fontSize:13, fontWeight:500, color:"#3A2E26" }}>{m.name}</span>
                        <span style={{ fontSize:10, background:tc.bg, color:tc.color, padding:"2px 8px", borderRadius:12, fontWeight:500 }}>{tc.label}</span>
                      </div>
                      <div style={{ fontSize:11, color:"#9A8070", marginTop:2 }}>{timeAgo(post.created_at)}</div>
                    </div>
                  </div>
                  <p style={{ fontSize:14, color:"#4A3C30", lineHeight:1.65, marginBottom:10 }}>{post.text}</p>
                  {post.photo_url && <div style={{ background:"#C8D8E8", borderRadius:10, height:130, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, fontSize:11, color:"#4A6050" }}>📸 Shared with family only</div>}

                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                    {Object.entries(post.reactions).map(([emoji, count]) => (
                      <button key={emoji} onClick={() => handleReact(post.id, emoji)} style={{ background: post.myReactions.includes(emoji) ? "rgba(139,90,60,0.12)" : "none", border: post.myReactions.includes(emoji) ? "0.5px solid rgba(139,90,60,0.35)" : "0.5px solid #D8CCB8", padding:"4px 10px", borderRadius:20, fontSize:13, display:"flex", alignItems:"center", gap:4, fontFamily:"inherit" }}>
                        <span>{emoji}</span><span style={{ fontSize:12, fontWeight:500 }}>{count}</span>
                      </button>
                    ))}
                    <div style={{ position:"relative", display:"inline-block" }}>
                      <button onClick={(e) => {
                        const picker = e.currentTarget.nextSibling;
                        picker.style.display = picker.style.display === 'flex' ? 'none' : 'flex';
                      }} style={{ ...S.ghostBtn, fontSize:11, padding:"3px 8px" }}>+</button>
                      <div style={{ display:"none", position:"absolute", bottom:"100%", left:0, background:"white", border:"0.5px solid #E4D8C8", borderRadius:10, padding:"6px", gap:2, flexWrap:"wrap", width:180, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", zIndex:10, marginBottom:4 }}>
                        {EMOJIS.map(e => (
                          <button key={e} onClick={() => handleReact(post.id, e)} style={{ fontSize:18, padding:"4px 6px", background:"none", border:"none", borderRadius:6 }}
                            onMouseOver={ev => ev.currentTarget.style.background='#F5EFE8'} onMouseOut={ev => ev.currentTarget.style.background='none'}>{e}</button>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => setExpandedPost(isExp ? null : post.id)} style={{ ...S.ghostBtn, fontSize:11, padding:"3px 10px" }}>
                      💬 {post.comments.length} {post.comments.length===1?'reply':'replies'}
                    </button>
                  </div>

                  {isExp && (
                    <div style={{ borderTop:"0.5px solid #E8D8C8", paddingTop:10 }}>
                      {post.comments.map((c, i) => {
                        const cm = getMember(c.member_id);
                        return (
                          <div key={c.id || i} style={{ display:"flex", gap:8, marginBottom:8, alignItems:"flex-start" }}>
                            <Avatar member={cm} size={26} />
                            <div style={{ background:"#F5EFE8", borderRadius:8, padding:"6px 10px", flex:1 }}>
                              <div style={{ fontSize:11, fontWeight:500, color:"#5A4030" }}>{cm.name?.split(' ')[0]}</div>
                              <div style={{ fontSize:13, color:"#4A3828", marginTop:2 }}>{c.text}</div>
                              <div style={{ fontSize:9, color:"#B0A090", marginTop:3 }}>{timeAgo(c.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:4 }}>
                        <Avatar member={profile} size={26} />
                        <input value={commentInputs[post.id]||''} onChange={e => setCommentInputs(p => ({...p,[post.id]:e.target.value}))}
                          onKeyDown={e => e.key==='Enter' && addComment(post.id)} placeholder="Write a reply…"
                          style={{ flex:1, background:"#F5EFE8", border:"0.5px solid #D8C8B0", borderRadius:8, padding:"7px 12px", fontSize:13, color:"#3A2E26", fontFamily:"inherit", outline:"none" }} />
                        <button style={{ ...S.primaryBtn, padding:"7px 14px", fontSize:12 }} onClick={() => addComment(post.id)}>Send</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!loadingPosts && posts.length === 0 && (
              <div style={{ textAlign:"center", padding:32, color:"#9A8070" }}>
                <div style={{ fontSize:32, marginBottom:8 }}>💛</div>
                <div style={{ fontSize:14 }}>No posts yet — be the first to share something!</div>
              </div>
            )}
          </div>
        )}

        {/* STUB TABS */}
        {activeTab !== 'home' && (
          <div style={{ textAlign:"center", padding:"48px 20px" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>{TABS.find(t => t.id===activeTab)?.icon}</div>
            <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:24, color:"#3A2E26", marginBottom:8 }}>{TABS.find(t => t.id===activeTab)?.label}</div>
            <div style={{ fontSize:13, color:"#9A8070", lineHeight:1.7, maxWidth:300, margin:"0 auto 24px" }}>Coming soon — being built at kulandercollins.com</div>
            <button style={{ ...S.primaryBtn }} onClick={() => setActiveTab('home')}>Back to Home</button>
          </div>
        )}
      </div>

      {/* NUDGE MODAL */}
      {nudgeTarget && (
        <div onClick={() => setNudgeTarget(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:"white", borderRadius:16, padding:22, width:"100%", maxWidth:390, maxHeight:"88vh", overflowY:"auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <Avatar member={nudgeTarget} size={42} />
              <div>
                <div style={{ fontFamily:"'Abril Fatface',serif", fontSize:20, color:"#3A2E26" }}>Nudge {nudgeTarget.name.split(' ')[0]}</div>
                <div style={{ fontSize:12, color:"#9A8070", marginTop:2 }}>Send them a warm reminder 💛</div>
              </div>
            </div>
            {NUDGE_REASONS.map((r, i) => (
              <button key={i} onClick={() => sendNudge(r)} style={{ display:"block", width:"100%", textAlign:"left", background:"#F5EFE8", border:"0.5px solid #E4D4C0", borderRadius:9, padding:"9px 12px", fontSize:12, color:"#4A3828", fontFamily:"inherit", marginBottom:7, transition:"background 0.15s" }}
                onMouseOver={e => e.currentTarget.style.background='#EDE3D5'} onMouseOut={e => e.currentTarget.style.background='#F5EFE8'}>
                {r}
              </button>
            ))}
            <button style={{ ...S.ghostBtn, width:"100%", marginTop:4 }} onClick={() => setNudgeTarget(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ROOT APP ──────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
        loadMembers();
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
        loadMembers();
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    const { data } = await supabase.from('family_members').select('*').eq('id', userId).single();
    if (data) setProfile(data);
  };

  const loadMembers = async () => {
    const { data } = await supabase.from('family_members').select('*').order('name');
    if (data) setMembers(data);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:"#F5EFE8", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ textAlign:"center", color:"#9A8070" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🏡</div>
          <div style={{ fontSize:14 }}>Loading family portal…</div>
        </div>
      </div>
    );
  }

  if (!user) return <LoginScreen />;
  if (!profile) return (
    <div style={{ minHeight:"100vh", background:"#F5EFE8", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ textAlign:"center", color:"#9A8070" }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🏡</div>
        <div style={{ fontSize:14 }}>Loading your profile…</div>
      </div>
    </div>
  );

  return <Portal user={user} profile={profile} members={members} onLogout={handleLogout} />;
}
